import classNames from 'classnames';
import React, { Fragment, useEffect, useImperativeHandle, useMemo } from 'react';
import { useSearchParam } from 'react-use';
import { useWizard, Wizard } from 'react-use-wizard';

import PaymentMethodStep from '@/mp/components/Payment/PaymentMethodStep';
import ProjectDetailsStep from '@/mp/components/Payment/ProjectDetailsStep';
import { usePaymentContext } from '@/mp/hooks/usePaymentContext';
import { useEnterPaymentStepMutation } from '@/redux/rtk';
import { captureError } from '@/utilities/error';
import { useHistory } from 'react-router-dom';
import BreadcrumbStep from '../../../components/wizard/BreadcrumbStep';
import Breadcrumbs from '../../../components/wizard/Breadcrumbs';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { setMembershipPriceId } from '../../../redux/actions/order';
import text from '../../../text';
import useUser from '../../hooks/useUser';
import ConfirmationStep from './ConfirmationStep';
import MembershipStep from './MembershipStep';
import { USER_TYPE } from '../../../constants/user';
import { isNil } from 'lodash';

// Single source of truth for all order steps
export const ORDER_STEPS = {
  CART: 'cart',
  MEMBER: 'member',
  PROJECT_DETAILS: 'projectDetails',
  PAYMENT_METHOD: 'paymentMethod',
  CONFIRMATION: 'confirmation',
} as const;
type OrderStepKey = (typeof ORDER_STEPS)[keyof typeof ORDER_STEPS];

// Array of all step keys in order
export const ORDER_STEPS_SEQUENCE = [
  ORDER_STEPS.CART,
  ORDER_STEPS.MEMBER,
  ORDER_STEPS.PROJECT_DETAILS,
  ORDER_STEPS.PAYMENT_METHOD,
  ORDER_STEPS.CONFIRMATION,
];

type OrderStep = {
  key: OrderStepKey;
  component: React.ReactNode;
  name: string;
  filter?: boolean | (() => boolean);
  disabled?: boolean;
};

const RedirectBack = () => {
  const history = useHistory();
  React.useEffect(() => {
    history.goBack();
  }, [history]);
  return null;
};

const useOrderSteps = (
  isRfqOrder: boolean = false
): { orderSteps: OrderStep[]; isLoading: boolean } => {
  const membershipPriceId = useAppSelector(
    (state) => state.order.membershipPriceId
  );
  const dispatch = useAppDispatch();
  const currentUser = useUser();
  const isEssentialUser =
    currentUser.isEssentialUser || currentUser.isGovernment;
  const isPretendFree = currentUser.pretendType === USER_TYPE.FREE;

  const showPaymentMethodStep = !currentUser.isStaff;
  const showMemberStep =
    (!currentUser.isStaff && !isEssentialUser) || isPretendFree;

  return {
    orderSteps: [
      {
        key: ORDER_STEPS.CART,
        component: <RedirectBack />,
        name: text(ORDER_STEPS.CART),
      },
      {
        key: ORDER_STEPS.MEMBER,
        component: (
          <MembershipStep
            nextButtonProps={{
              label: text('continueProjectDetails'),
            }}
            previousButtonProps={{
              label: text('previous'),
            }}
            membershipPriceId={membershipPriceId}
            onMembershipChange={(membershipPriceId) => {
              dispatch(setMembershipPriceId(membershipPriceId));
            }}
          />
        ),
        name: text(ORDER_STEPS.MEMBER),
        filter: showMemberStep,
      },
      {
        key: ORDER_STEPS.PROJECT_DETAILS,
        component: (
          <ProjectDetailsStep
            nextLabel={
              isRfqOrder
                ? text('requestQuote')
                : showPaymentMethodStep
                  ? text('continueToPaymentMethod')
                  : text('continueToConfirm')
            }
          />
        ),
        name: text(ORDER_STEPS.PROJECT_DETAILS),
      },
      {
        key: ORDER_STEPS.PAYMENT_METHOD,
        component: <PaymentMethodStep nextLabel={text('continueToConfirm')} />,
        name: text(ORDER_STEPS.PAYMENT_METHOD),
        filter: showPaymentMethodStep && !isRfqOrder,
      },
      {
        key: ORDER_STEPS.CONFIRMATION,
        component: <ConfirmationStep />,
        name: text(ORDER_STEPS.CONFIRMATION),
        filter: !isRfqOrder,
      },
    ].filter((s) => s.filter ?? true),
    isLoading: currentUser.isLoading,
  };
};

const OrderWizard = ({
  isOnlyBreadcrumbs = false,
  isVisible = true,
  defaultStep,
  isRfqOrder = false,
}: {
  isOnlyBreadcrumbs?: boolean;
  isVisible?: boolean;
  defaultStep?: number | null;
  isRfqOrder?: boolean;
}) => {
  const { orderSteps, isLoading } = useOrderSteps(isRfqOrder);

  // -1 when the wizard doesn't render PaymentMethodStep (staff / RFQ), in
  // which case the quoter stamp is handled by _handleMap2dVersionUpdate or
  // the webhook, not by this trigger.
  const paymentMethodIndex = orderSteps.findIndex(
    (s) => s.key === ORDER_STEPS.PAYMENT_METHOD
  );

  const stepIndexQueryParam = useSearchParam('step');
  const startingStepIndex = useMemo(() => {
    if (!isNil(defaultStep)) {
      return defaultStep;
    }
    if (stepIndexQueryParam) {
      const parsed = Number.parseInt(stepIndexQueryParam);
      if (!Number.isNaN(parsed)) {
        if (parsed < orderSteps.length) {
          return parsed;
        }
      }
    }
    return 1;
  }, [stepIndexQueryParam, orderSteps.length]);

  return (
    <div className={classNames('OrderWizard', isVisible && 'visible')}>
      <Wizard
        key={`${isLoading ? 'loading' : 'loaded'}-${orderSteps.length}`}
        startIndex={startingStepIndex}
        header={
          <>
            {!isOnlyBreadcrumbs && paymentMethodIndex >= 0 && (
              <EnterPaymentStepTrigger />
            )}
            <Breadcrumbs>
              {orderSteps.map((step, index) => (
                <BreadcrumbStep
                  key={index}
                  stepIndex={index}
                  stepLabel={(index + 1).toString()}
                  name={step.name}
                  disabled={step.disabled || isOnlyBreadcrumbs}
                />
              ))}
            </Breadcrumbs>
          </>
        }
      >
        {orderSteps.map(({ component }, index) => (
          <Fragment key={index}>
            <PopUpOrderWizard isOnlyBreadcrumbs={isOnlyBreadcrumbs} />
            {!isOnlyBreadcrumbs && component}
          </Fragment>
        ))}
      </Wizard>
    </div>
  );
};

const PopUpOrderWizard = ({
  isOnlyBreadcrumbs,
}: {
  isOnlyBreadcrumbs: boolean;
}) => {
  const wizardFunctions = useWizard();
  // only attempt to use PaymentContext when not in breadcrumb-only mode
  // ensures we fail fast if context is missing when it's actually needed
  if (!isOnlyBreadcrumbs) {
    const { wizardFunctions: wizardFunctionsRef } = usePaymentContext();
    useImperativeHandle(wizardFunctionsRef, () => wizardFunctions);
  }
  return null;
};

const EnterPaymentStepTrigger = () => {
  const { jobId, committedQuoteId } = usePaymentContext();
  const [enterPaymentStep] = useEnterPaymentStepMutation();

  useEffect(() => {
    if (!committedQuoteId) return;
    enterPaymentStep({ jobId, data: { quoteId: committedQuoteId } })
      .unwrap()
      .catch(captureError);
  }, [jobId, committedQuoteId, enterPaymentStep]);

  return null;
};

export default OrderWizard;
